/**
 * ============================================================================
 * EXPERIMENT: Spring Flex Offer (ab_flex_offer)
 * PR: #2760
 *
 * Temporary badge shown on dossiers eligible to the "Spring Flex" offer.
 * Gated by the API field `dossier.flexOfferEligible` — no feature flag.
 * Sunsets 2026-10-31 — delete this folder in one PR.
 *
 * Find all experiment code: grep -r "ab_flex_offer" src/
 * ============================================================================
 */

import {
    Chip,
    Link,
    paletteColors,
    Stack,
    styled,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
} from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { useGetDossierData } from '../../../resources/dossier/queries';
import { DossierId } from '../../../resources/dossier/types';

const LEARN_MORE_URL =
    'https://go.meltingspot.io/spot/73df679f-6579-4937-83c0-70212b61a9d2/documents/7f2a7f9b-d476-432c-9c6d-eff7dd2eca3a?from=c86e6c09-6c9d-4c0e-b40d-befb79ba561c';

const SpringFlexTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 320,
        backgroundColor: paletteColors.neutral[900],
        padding: 12,
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: paletteColors.neutral[900],
    },
});

type Props = {
    dossierId: DossierId;
};

export function AB_SpringFlexBadge({ dossierId }: Readonly<Props>) {
    const { formatMessage } = useIntl();
    const dossierData = useGetDossierData(dossierId);

    if (!dossierData?.dossier?.flexOfferEligible) {
        return null;
    }

    return (
        <SpringFlexTooltip
            arrow
            placement="bottom-start"
            title={
                <Stack gap={1}>
                    <Typography variant="subtitle2" fontWeight={700} color="common.white">
                        {formatMessage({ id: 'spring_flex.popover.title' })}
                    </Typography>
                    <Typography variant="body2" color="common.white">
                        {formatMessage({ id: 'spring_flex.popover.body' })}
                    </Typography>
                    <Link
                        href={LEARN_MORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="always"
                        color="inherit"
                        sx={{
                            fontWeight: 700,
                            color: 'common.white',
                            '&:hover': { color: paletteColors.neutral[300] },
                        }}
                    >
                        {formatMessage({ id: 'spring_flex.popover.learn_more' })}
                    </Link>
                </Stack>
            }
        >
            <Chip
                data-spec-id="spring-flex-badge"
                label={formatMessage({ id: 'spring_flex.badge.label' })}
                sx={{
                    backgroundColor: paletteColors.green[100],
                    color: paletteColors.green[800],
                    fontWeight: 700,
                    borderRadius: 64,
                }}
            />
        </SpringFlexTooltip>
    );
}
